<template>
  <!-- Proposal wrapper -->
  <div class="is-public w-full">
    <LoadingModal
      heading="Loading Proposal"
      :loading="loading" />
    <!-- Content sections -->
    <section
      v-if="store.proposal.accepted || store.proposal.accepted_sibling_id"
      class="print:hidden">
      <div class="flex justify-center gap-x-6 bg-indigo-600 px-6 py-2.5 sm:px-3.5">
        <p class="text-sm leading-6 text-white">
          <strong
            v-if="store.proposal.accepted_sibling_id"
            class="font-semibold">
            Another version of this proposal (v{{ versions?.find(v => v.proposalVersionId === store.proposal.accepted_sibling_id)?.proposalVersionNumber ?? 0 }}, id{{ versions?.find(v => v.proposalVersionId === store.proposal.accepted_sibling_id)?.proposalVersionId ?? 0 }}) is accepted
          </strong>
          <strong
            v-else
            class="font-semibold">
            This version of the proposal (v{{ proposalVersions?.find(v => v.proposalVersionId === store.proposal.proposalVersionId)?.proposalVersionNumber ?? 0 }}, id{{ proposalVersions?.find(v => v.proposalVersionId === store.proposal.proposalVersionId)?.proposalVersionId ?? 0 }}) is accepted
          </strong>
        </p>
      </div>
    </section>
    <ProposalHero @scroll-into-view="(v) => $refs[v.component].$el.scrollIntoView({ behavior: 'smooth' })" />
    <ProposalIntro ref="ProposalIntroComponent" />
    <ProposalTransport ref="ProposalTransportComponent" />
    <ProposalLodgings ref="ProposalLodgingsComponent" />
    <ProposalProgram ref="ProposalProgramComponent" />
    <ProposalActivities ref="ProposalActivitiesComponent" />
    <ProposalEateries ref="ProposalEateriesComponent" />
    <ProposalMediaGallery ref="ProposalMediaGalleryComponent" />
    <!-- // TODO: Create ProposalInteractiveMap when dynamic data is available -->
    <!-- <ProposalInteractiveMap ref="ProposalInteractiveMapComponent" /> -->
    <ProposalPrices
      ref="ProposalPricesComponent"
      :proposal-versions="proposalVersions" />
  </div>
  <!-- /Proposal wrapper -->
</template>

<script setup>
/* external */
import {
  onBeforeMount, onBeforeUnmount,
  onMounted, ref,
} from 'vue';
import Cookies from 'js-cookie';

/* local */
import { useProposalStore } from '@/Stores/Proposal';
import ProposalHero from '@/Pages/Proposal/Show/ProposalHero.vue';
import ProposalIntro from '@/Pages/Proposal/Show/ProposalIntro.vue';
import ProposalTransport from '@/Pages/Proposal/Show/ProposalTransport.vue';
import ProposalLodgings from '@/Pages/Proposal/Show/ProposalLodgings.vue';
import ProposalProgram from '@/Pages/Proposal/Show/ProposalProgram.vue';
import ProposalActivities from '@/Pages/Proposal/Show/ProposalActivities.vue';
import ProposalEateries from '@/Pages/Proposal/Show/ProposalEateries.vue';
import ProposalMediaGallery from '@/Pages/Proposal/Show/ProposalMediaGallery.vue';
import ProposalInteractiveMap from '@/Pages/Proposal/Show/ProposalInteractiveMap.vue';
import ProposalPrices from '@/Pages/Proposal/Show/ProposalPrices.vue';
import LoadingModal from '@/Components/Modals/LoadingModal.vue';

/* *********************************************************************
 * Page declarations
 * ****************************************************************** */

const props = defineProps({
  databaseProposal: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  databaseProposalVersions: {
    type: Array,
    required: true,
    default: () => [],
  },
});

// get current language from cookie, or default to 'no'
const currentLanguage = ref(Cookies.get('currentLanguage') ?? 'no');

const store = useProposalStore();

const ProposalIntroComponent = ref(null);
const ProposalTransportComponent = ref(null);
const ProposalLodgingsComponent = ref(null);
const ProposalProgramComponent = ref(null);
const ProposalPricesComponent = ref(null);

const proposalVersions = ref(props.databaseProposalVersions);

/* *********************************************************************
 * Page functions
 * ****************************************************************** */

/**
 * Update current language cookie
 * @param {string} value - language code
 */
function updateCurrentLanguage(value) {
  Cookies.set('currentLanguage', value, {
    expires: 30, secure: false, sameSite: 'lax',
  });
  currentLanguage.value = value;
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

// loading should only be active on first load
const loading = ref(document.readyState === 'interactive' ?? false);

onBeforeMount(() => {
  // Reset store
  store.$reset();
  // Set store with data from database
  store.setProposal({
    ...props.databaseProposal.data,
    accepted: props.databaseProposal.accepted,
    accepted_at: props.databaseProposal.accepted_at,
    verification_email_sent: props.databaseProposal.verification_email_sent,
    accepted_data: props.databaseProposal.accepted_data,
    terms: props.databaseProposal.terms,
    hash: props.databaseProposal.hash,
    proposalVersionId: props.databaseProposal.id,
    accepted_sibling_id: props.databaseProposal.accepted_sibling_id,
  }, true);
});

onMounted(() => {
  // LoadingModal - if first load, we need to stop loading when completed
  document.onreadystatechange = () => {
    if (document.readyState === 'complete') {
      setTimeout(() => {
        loading.value = false;
      }, 500);
    }
  };
});

onBeforeUnmount(() => {
  // Reset store
  store.$reset();
});
</script>

<script>
/* eslint-disable */
  import EmptyLayout from '@/Layouts/EmptyLayout';
  export default { layout: EmptyLayout };
/* eslint-enable */
</script>
